import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {PromoService} from '../../../_services/promo.service';
import {Promo, PromoAutoReward, PromoValidateResult} from '../../../_models/promo';
import {Discount} from '../../../_models/discount';
import {LineItem} from '../../../_models/lineItem';
import {NotifierService} from 'angular-notifier';

/**
 * Selection emitted by <app-checkout-rewards>. Parent components apply this
 * to the order at submit time and call the appropriate promoService methods
 * (redeem / redeemAutoReward / recordVisit) after the order succeeds.
 */
export interface CheckoutRewardsSelection {
    customerNumber: string | null;
    /** discount derived from the chosen reward or manual promo (null = no reward chosen) */
    discount: Discount | null;
    /** validated manual promo (mutually exclusive with autoReward) */
    manualPromo: PromoValidateResult | null;
    /** auto-reward chosen by user (mutually exclusive with manualPromo) */
    autoReward: PromoAutoReward | null;
    /** When true, parent should call promoService.recordVisit after order success */
    recordVisit: boolean;
}

@Component({
    selector: 'app-checkout-rewards',
    templateUrl: './checkout-rewards.component.html',
    styleUrls: ['./checkout-rewards.component.scss']
})
export class CheckoutRewardsComponent implements OnInit, OnChanges {
    /** Order total in cents — used to compute manual promo discount preview */
    @Input() orderTotal = 0;
    /** Cart line items — used to detect whether a FREE_ITEM reward is in cart */
    @Input() lineItems: LineItem[] = [];
    /** Optional: prefill phone number (e.g. captured upstream by kiosk) */
    @Input() prefillPhone: string | null = null;
    /** When false, auto-rewards section is hidden (manual promo codes still work) */
    @Input() rewardsEnabled = true;

    @Output() selectionChange = new EventEmitter<CheckoutRewardsSelection>();

    phoneNumber = '';
    lookupLoading = false;
    lookupDone = false;
    autoRewards: PromoAutoReward[] = [];
    /** Currently selected reward identifier: promoId of an auto-reward, 'none' for skip, or null until choice made */
    selectedRewardKey: string | null = null;

    promoCodeInput = '';
    promoValidating = false;
    promoApplied = false;
    appliedPromo: PromoValidateResult | null = null;

    // Intro promo
    introPromo: Promo | null = null;
    introConfig: any = null;
    introPromoSelected = false;

    constructor(private promoService: PromoService, private notifier: NotifierService) {}

    ngOnInit() {
        if (this.prefillPhone && this.prefillPhone.length >= 10) {
            this.phoneNumber = this.prefillPhone;
            this.lookupCustomer();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['lineItems'] && this.selectedRewardKey) {
            // Re-emit so parent recomputes (e.g. free-item-in-cart status changed)
            this._emit();
        }
    }

    lookupCustomer() {
        if (!this.phoneNumber || this.phoneNumber.length < 10) {
            this.notify('warning', 'Enter a valid phone number');
            return;
        }
        this.lookupLoading = true;
        this.promoService.checkAutoRewards(this.phoneNumber).subscribe(
            rewards => {
                this.lookupLoading = false;
                this.lookupDone = true;
                this.autoRewards = rewards || [];
                // Default selection: first reward if any, else 'none'
                this.selectedRewardKey = this.autoRewards.length > 0 ? this.autoRewards[0].promoId : 'none';

                // Fetch intro promo if no existing rewards (likely new customer)
                if (this.autoRewards.length === 0) {
                    this.promoService.getIntroPromo().subscribe(
                        result => {
                            if (result?.promo) {
                                this.introPromo = result.promo;
                                this.introConfig = result.introConfig;
                                this.introPromoSelected = true;
                            }
                            this._emit();
                        },
                        () => this._emit()
                    );
                } else {
                    this._emit();
                }
            },
            () => {
                this.lookupLoading = false;
                this.lookupDone = true;
                this.autoRewards = [];
                this.selectedRewardKey = 'none';
                this._emit();
            }
        );
    }

    clearCustomer() {
        this.phoneNumber = '';
        this.lookupDone = false;
        this.autoRewards = [];
        this.selectedRewardKey = null;
        this._emit();
    }

    onRewardChange(key: string) {
        this.selectedRewardKey = key;
        this._emit();
    }

    applyPromoCode() {
        if (!this.promoCodeInput?.trim()) return;
        this.promoValidating = true;
        this.promoService.validate(this.promoCodeInput.trim()).subscribe(
            result => {
                this.promoValidating = false;
                if (result.valid) {
                    this.appliedPromo = result;
                    this.promoApplied = true;
                    this._emit();
                } else {
                    this.notify('error', result.message || 'Invalid promo code');
                }
            },
            () => {
                this.promoValidating = false;
                this.notify('error', 'Failed to validate promo code');
            }
        );
    }

    removePromoCode() {
        this.promoApplied = false;
        this.promoCodeInput = '';
        this.appliedPromo = null;
        this._emit();
    }

    /** Hint shown next to a FREE_ITEM reward when the qualifying item isn't in the cart */
    isFreeItemInCart(reward: PromoAutoReward): boolean {
        if (reward.rewardType !== 'FREE_ITEM') return true;
        return !!this._findFreeLineItem(reward);
    }

    appliedPromoDisplay(): string {
        if (!this.appliedPromo) return '';
        return this.appliedPromo.discountType === 'PERCENT'
            ? `${this.appliedPromo.discountValue}% off`
            : `$${(this.appliedPromo.discountValue / 100).toFixed(2)} off`;
    }

    private _findFreeLineItem(reward: PromoAutoReward): LineItem | null {
        if (!this.lineItems) return null;
        if (reward.freeItemType === 'ITEM') {
            return this.lineItems.find(li => (li as any).item?.id === reward.freeItemId) || null;
        }
        return this.lineItems.find(li => li.categories?.some(c => (c as any).id === reward.freeItemId)) || null;
    }

    private _selectedAutoReward(): PromoAutoReward | null {
        if (!this.selectedRewardKey || this.selectedRewardKey === 'none') return null;
        return this.autoRewards.find(r => r.promoId === this.selectedRewardKey) || null;
    }

    private _buildAutoRewardDiscount(reward: PromoAutoReward): Discount | null {
        if (reward.rewardType === 'VISIT_REWARD') {
            const d = new Discount();
            d.name = reward.promoName;
            if (reward.discountType === 'PERCENT') {
                d.percentage = reward.discountValue;
            } else {
                d.amount = -(reward.discountValue);
            }
            return d;
        }
        if (reward.rewardType === 'FREE_ITEM') {
            const freeItem = this._findFreeLineItem(reward);
            if (!freeItem) return null;
            const d = new Discount();
            d.name = 'Free ' + (reward.freeItemName || 'Item');
            d.amount = -(freeItem.price);
            return d;
        }
        return null;
    }

    private _buildIntroPromoDiscount(promo: Promo): Discount {
        const d = new Discount();
        d.name = promo.name;
        if (promo.discountType === 'PERCENT') {
            d.percentage = promo.discountValue;
        } else {
            d.amount = -(promo.discountValue);
        }
        return d;
    }

    private _buildManualPromoDiscount(p: PromoValidateResult): Discount {
        const d = new Discount();
        d.name = p.promoName;
        if (p.discountType === 'PERCENT') {
            d.percentage = p.discountValue;
        } else {
            d.amount = -(p.discountValue);
        }
        return d;
    }

    private _emit() {
        // Manual promo wins if applied; otherwise use selected auto-reward; otherwise intro promo.
        let discount: Discount | null = null;
        let autoReward: PromoAutoReward | null = null;

        if (this.promoApplied && this.appliedPromo) {
            discount = this._buildManualPromoDiscount(this.appliedPromo);
        } else {
            autoReward = this._selectedAutoReward();
            if (autoReward) {
                discount = this._buildAutoRewardDiscount(autoReward);
            } else if (this.introPromoSelected && this.introPromo) {
                discount = this._buildIntroPromoDiscount(this.introPromo);
            }
        }

        const selection: CheckoutRewardsSelection = {
            customerNumber: this.phoneNumber && this.lookupDone ? this.phoneNumber : null,
            discount,
            manualPromo: this.promoApplied ? this.appliedPromo : null,
            autoReward,
            // We always count a visit for an identified customer, even if they
            // chose 'none' (skip the reward but still earn future visits).
            recordVisit: !!(this.phoneNumber && this.lookupDone)
        };
        this.selectionChange.emit(selection);
    }

    private notify(type: string, msg: string) {
        try {
            this.notifier.notify(type, msg);
        } catch (e) {
            console.warn('[CheckoutRewards]', type, msg);
        }
    }
}
